import React, { useState, useEffect } from "react";
import "../style/css/TelegramBot.css";
import api from "../api.js";
import Toast, { ToastType } from "./Toast";

interface WhatsAppSettings {
  reminder_hours: number;
  confirmation_message: string;
  response_after_confirm: string;
}

interface WhatsAppData {
  connected: boolean;
  phoneNumber: string;
  whatsappLink: string;
  settings: WhatsAppSettings;
  monthlyRemindersSent: number;
}

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: WhatsAppSettings;
  onSave: (settings: WhatsAppSettings) => Promise<void>;
  phoneNumber: string;
}

// Icons (same as before)
const ConnectedIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M5 12l5 5L20 7" />
  </svg>
);

const DisconnectedIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const MessageIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const TextIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z" />
    <path d="M10 9h8M10 12h8M10 15h6" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
  </svg>
);

// Updated Edit Modal with new inputs
const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSave,
  phoneNumber,
}) => {
  const [formSettings, setFormSettings] = useState<WhatsAppSettings>(settings);
  const [hoursError, setHoursError] = useState(false);
  const [charError, setCharError] = useState(false);

  // ✅ Re-sync state when modal opens with fresh settings
  useEffect(() => {
    if (isOpen) {
      setFormSettings(settings);
      setHoursError(false);
    }
  }, [isOpen, settings]);

  if (!isOpen) return null;

  const validateHours = (value: string) => {
    const num = parseInt(value);
    return num > 0 && num <= 150;
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || validateHours(value)) {
      setHoursError(false);
      setFormSettings((prev) => ({
        ...prev,
        reminder_hours: parseInt(value) || 0,
      }));
    } else {
      setHoursError(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formSettings.reminder_hours > 0 && formSettings.reminder_hours <= 150) {
      onSave(formSettings);
      onClose();
    }
  };

  const handleHoursKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: digits 0-9, Backspace, Delete, Tab, Enter, Arrow keys
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ];

    const isDigit = /^[0-9]$/.test(e.key);
    const isAllowed = allowedKeys.includes(e.key);

    if (!isDigit && !isAllowed) {
      e.preventDefault();
      setCharError(true);
      setTimeout(() => setCharError(false), 2000);
    } else {
      setCharError(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Editar Configuración</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="bot-info">
          <div className="bot-info-icon">
            <MessageIcon />
          </div>
          <div>
            <h4>{phoneNumber}</h4>
            <p className="text-muted">¡Escríbenos!</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="input-label">
              <div className="input-title">
                <ClockIcon />
                <span>Anticipación para enviar recordatorios</span>
              </div>
              <p className="input-description">
                Número de horas antes del evento
              </p>
              <input
                type="number"
                min="1"
                max="150"
                value={formSettings.reminder_hours || ""}
                onChange={handleHoursChange}
                onKeyDown={handleHoursKeyDown} // 👈 add this
                className={`form-input ${hoursError || charError ? "input-error" : ""}`}
                placeholder="Ej: 2"
              />

              {/* Show one error at a time, charError takes priority */}
              {charError && (
                <p className="input-error-text">
                  Solo se permiten números enteros
                </p>
              )}
              {hoursError && !charError && (
                <p className="input-error-text">
                  Debe ser un número entre 1 y 150
                </p>
              )}
            </label>
          </div>

          <div className="form-group">
            <label className="input-label">
              <div className="input-title">
                <TextIcon />
                <span>Mensaje para la confirmación de tu paciente</span>
              </div>
              <p className="input-description">
                Mensaje personalizado que se enviará a tus pacientes
              </p>
              <textarea
                value={formSettings.confirmation_message}
                readOnly
                className="form-textarea form-textarea--readonly"
                rows={3}
              />
            </label>
          </div>

          <div className="form-group">
            <label className="input-label">
              <div className="input-title">
                <TextIcon />
                <span>Respuesta tras confirmar</span>
              </div>
              <p className="input-description">
                Lo que el bot dirá cuando el paciente confirme
              </p>
              <textarea
                value={formSettings.response_after_confirm}
                onChange={(e) =>
                  setFormSettings((prev) => ({
                    ...prev,
                    response_after_confirm: e.target.value,
                  }))
                }
                placeholder="¡Excelente! Te esperamos."
                className="form-textarea"
                rows={3}
              />
            </label>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={hoursError || formSettings.reminder_hours === 0}
            >
              Guardar cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Component (updated settings display)
const WhatsAppReminders: React.FC = () => {
  const [whatsappData, setWhatsappData] = useState<WhatsAppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);

  useEffect(() => {
    fetchWhatsappData();
  }, []);

  const fetchWhatsappData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/profiles/profile_whatsapp_reminders/");
      const data: WhatsAppData = response.data;
      setWhatsappData(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    if (whatsappData?.whatsappLink) {
      window.open(whatsappData.whatsappLink, "_blank", "noopener,noreferrer");
    }
  };

  const handleSaveSettings = async (newSettings: WhatsAppSettings) => {
    try {
      console.log("newSettings ", newSettings);
      const response = await api.post(
        `profiles/change_config_whatsapp/`,
        newSettings,
      );

      // Update local state with the returned data from server
      setWhatsappData((prev) =>
        prev ? { ...prev, settings: newSettings } : null,
      );
      setToast({
        message: "Configuración actualizada correctamente",
        type: "success",
      });
    } catch (error) {
      setToast({
        message: "Hubo un error al guardar los cambios",
        type: "error",
      });
    }
  };

  if (loading) {
    return (
      <div className="telegram-bot-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando configuración de WhatsApp...</p>
        </div>
      </div>
    );
  }

  if (!whatsappData) {
    return (
      <div className="telegram-bot-container">
        <div className="error-state">
          <MessageIcon />
          <p>Error al cargar los datos de WhatsApp</p>
          <button className="btn btn--primary" onClick={fetchWhatsappData}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="telegram-bot-container">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="telegram-bot-card">
        <div className="bot-header">
          <div
            className="bot-icon-wrapper"
            style={{ background: "linear-gradient(135deg, #25D366, #128C7E)" }}
          >
            <WhatsAppIcon />
          </div>
          <div>
            <h2>Recordatorios WhatsApp</h2>
            <div className="status-badge">
              {whatsappData.connected ? (
                <>
                  <ConnectedIcon />
                  <span className="status-connected">
                    Conectado a {whatsappData.phoneNumber}
                  </span>
                </>
              ) : (
                <>
                  <DisconnectedIcon />
                  <span className="status-disconnected">No conectado</span>
                </>
              )}
            </div>
          </div>
        </div>

        {!whatsappData.connected ? (
          <div className="connection-prompt">
            <p className="prompt-text">
              Conecta WhatsApp para recibir recordatorios directamente en tu
              teléfono.
            </p>
            {/*<button
              className="btn btn--primary btn--large"
              onClick={handleConnect}
            >
              <WhatsAppIcon />
              Conectar WhatsApp
            </button>
            <p className="help-text">
              Escanea el código QR o envía un mensaje para completar la
              conexión.
            </p>*/}
          </div>
        ) : (
          <div className="bot-settings">
            <div className="settings-header">
              <h3>Configuración de WhatsApp</h3>
              <button
                className="btn btn--secondary btn--small"
                onClick={() => setIsModalOpen(true)}
              >
                <EditIcon />
                Editar configuración
              </button>
            </div>

            <div className="settings-list">
              <div className="setting-item active">
                <div className="setting-icon">
                  <ClockIcon />
                </div>
                <div className="setting-content">
                  <h4>Anticipación para enviar recordatorios</h4>
                  <p>
                    {whatsappData.settings.reminder_hours} horas antes del
                    evento
                  </p>
                </div>
                <div className="setting-status">
                  <span className="status-dot on"></span>
                  {whatsappData.settings.reminder_hours}h
                </div>
              </div>

              <div className="setting-item active">
                <div className="setting-icon">
                  <TextIcon />
                </div>
                <div className="setting-content">
                  <h4>Mensaje para la confirmación de tu paciente</h4>
                  <p>
                    {whatsappData.settings.confirmation_message ||
                      "Sin mensaje personalizado"}
                  </p>
                </div>
                <div className="setting-status">
                  <span className="status-dot on"></span>
                  Personalizado
                </div>
              </div>
              <div className="setting-item active">
                <div className="setting-icon">
                  <TextIcon />
                </div>
                <div className="setting-content">
                  <h4>Respuesta post-confirmación</h4>
                  <p>
                    {whatsappData.settings.response_after_confirm ||
                      "Sin mensaje personalizado"}
                  </p>
                </div>
                <div className="setting-status">
                  <span className="status-dot on"></span>
                  Personalizado
                </div>
              </div>

              <div className="setting-item stats-item">
                <div className="setting-icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22,4 12,14.01 9,11.01" />
                  </svg>
                </div>
                <div className="setting-content">
                  <h4>Número de recordatorios enviados este mes</h4>
                  <p>
                    {whatsappData.monthlyRemindersSent.toLocaleString()}{" "}
                    mensajes
                  </p>
                </div>
                <div className="setting-status">
                  <span className="status-dot on"></span>
                  Este mes
                </div>
              </div>
            </div>
          </div>
        )}

        <EditModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          settings={whatsappData.settings}
          onSave={handleSaveSettings}
          phoneNumber={whatsappData.phoneNumber}
        />
      </div>
    </div>
  );
};

export default WhatsAppReminders;
